/**
 * @fileoverview Application Entry Point for Clockwise CPA Client
 *
 * This is the main entry point for the React application, responsible for:
 * - Setting up the React root DOM element
 * - Configuring React Router with future flags
 * - Wrapping the application with necessary context providers
 * - Loading global CSS styles and frameworks
 * - Bootstrapping the entire application
 *
 * Provider Hierarchy (from outermost to innermost):
 * 1. SnackbarProvider - Global notification system
 * 2. AppProvider - Consolidated app contexts (Organization, Utils, Notify, Auth, Search, Accessibility, Navigation)
 * 3. RouterProvider - React Router navigation
 *
 * Dependencies:
 * - React 18+ with createRoot API
 * - React Router v6+ with browser router
 * - Notistack for notifications
 * - Bootstrap for styling
 * - Custom context providers for state management
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/theme-variables.css'; // Global theme variables - must be first
import './styles/index.css';
import './styles/organization-theme.css';
import './styles/mobile-utilities.css';
import './styles/GlobalSearch.css';
import './styles/ChristmasTheme.css'; // Theme decorations
import './styles/HalloweenTheme.css'; // Theme decorations
import './styles/OceanTheme.css';
import './styles/SunsetTheme.css';
import './styles/ForestTheme.css';
import './styles/LavenderTheme.css';
import './styles/RosegoldTheme.css';
import './styles/MidnightTheme.css';
import './styles/AutumnTheme.css';
import './styles/SpringTheme.css';
import './styles/MonochromeTheme.css';
import './styles/PublicStatusPage.css'; // Pre-load for status page
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import routes from './routes';
import { SnackbarProvider } from 'notistack';
import { AppProvider } from './context/AppProvider';
import 'bootstrap/dist/css/bootstrap.min.css';
import { initializeGlobalTracker } from './utils/ActivityTracker';

// Initialize React 18 root for concurrent features
const root = ReactDOM.createRoot(document.getElementById('root'));

// Configure React Router with future flags for forward compatibility
const router = createBrowserRouter(routes, {
  future: {
    v7_startTransition: true,     // Enable React 18 transitions
    v7_relativeSplatPath: true    // New relative path behavior
  }
});

// Initialize activity tracking
// Enable in production or when explicitly enabled via environment variable
if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_TRACKING === 'true') {
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Initializing global activity tracker...');
  }
  initializeGlobalTracker({
    enabled: true,
    batchSize: 10,
    batchInterval: 30000 // 30 seconds
  });
  if (process.env.NODE_ENV === 'development') {
    console.log('✅ Activity tracker initialized');
  }
}



/**
 * Render the application with consolidated providers
 *
 * Simplified provider structure:
 * - SnackbarProvider: Global notifications (outermost, no dependencies)
 * - AppProvider: All application contexts consolidated (maintains internal dependency order)
 * - RouterProvider: React Router navigation (innermost, depends on all contexts)
 *
 * The AppProvider internally manages the correct dependency order for all contexts:
 * Organization → Utils → Notify → Auth → Accessibility → Search
 * NavigationProvider is handled separately in App.js since it requires Router context
 *
 * Note: StrictMode is disabled in production to prevent double useEffect calls
 *
 * Special handling: Public status page doesn't need auth context, so skip AppProvider
 */

// Check if we're on the public status page (no auth needed)
// Supports both /status path and status.clockwisecpa.app subdomain
const hostname = window.location.hostname;
const isStatusSubdomain = hostname.startsWith('status.') || hostname === 'status.localhost';
const isStatusPath = window.location.pathname === '/status';
const isStatusPage = isStatusSubdomain || isStatusPath;

// If on status subdomain, redirect to root path (the status page will render there)
if (isStatusSubdomain && window.location.pathname !== '/') {
  window.location.pathname = '/';
}

// Styled loading fallback for status page
const StatusPageLoader = () => (
  <div className="public-status-page">
    <div className="status-client-header">
      <div className="status-header-row">
        <div className="status-title-section">
          <div className="status-client-title">
            <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="status-client-icon" height="1em" width="1em"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
            <h1>System Status</h1>
          </div>
        </div>
      </div>
    </div>
    <div className="status-container">
      <div className="status-loading">
        <svg className="spin" stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
        <p>Loading status...</p>
      </div>
    </div>
  </div>
);

// Create status-only routes for subdomain
const statusRoutes = isStatusSubdomain ? [
  {
    path: '/',
    element: <React.Suspense fallback={<StatusPageLoader />}>{React.createElement(React.lazy(() => import('./pages/PublicStatusPage')))}</React.Suspense>
  },
  {
    path: '*',
    element: <React.Suspense fallback={<StatusPageLoader />}>{React.createElement(React.lazy(() => import('./pages/PublicStatusPage')))}</React.Suspense>
  }
] : routes;

const statusRouter = createBrowserRouter(statusRoutes, {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
});

root.render(
  // REMOVE STRICTMODE IN PROD SO ALL USEEFFECT FETCHES DONT HAPPEN TWICE
  // <React.StrictMode>
  <SnackbarProvider autoHideDuration={3000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
    {isStatusPage ? (
      <RouterProvider router={statusRouter}>
        {/* Public status page - no auth context needed */}
      </RouterProvider>
    ) : (
      <AppProvider>
        <RouterProvider router={router}>
          {/* App component is rendered through routes */}
        </RouterProvider>
      </AppProvider>
    )}
  </SnackbarProvider>
  // </React.StrictMode>
);
